import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../hooks/useLanguage'
import { authService } from '../services/authService'
import { UserBookings } from '../components/UserBookings'
import { AddressForm } from '../components/AddressForm'
import { ChangePasswordForm } from '../components/ChangePasswordForm'
import UserProductOrders from '../components/UserProductOrders'

const AccountPage: React.FC = () => {
  const { t } = useLanguage()
  const { user, logout, isLoading, refreshUser } = useAuth()
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState('')

  const [deleteError, setDeleteError] = useState('')
  const navigate = useNavigate()
  
  const handleUserUpdate = () => {
    refreshUser();
  };
  
  const handleLogout = () => {
    logout()
    // Redirect alla homepage dopo logout
    navigate('/')
  }

  const handleDeleteAccount = async () => {
    if (!user) return
    
    if (deleteConfirmation !== 'delete') {
      setDeleteError(t('account.deleteAccountInputError'))
      return
    }


    
    setIsDeleting(true)
    try {
      const result = await authService.deleteAccount()

      // Logout e redirect alla homepage
      logout()
      navigate('/')
    } catch (error) {
      console.error('Error deleting account:', error)
      setIsDeleting(false)
      setDeleteError('Failed to delete account. Please check your password and try again.')
    }
  }

  const resetDeleteModal = () => {
    setShowDeleteModal(false)
    setDeleteConfirmation('')

    setDeleteError('')
  }

  // Mostra loading mentre verifica l'autenticazione
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento...</p>
        </div>
      </div>
    )
  }

  // Se non c'è nessun utente autenticato, redirect al login
  if (!user) {
    navigate('/login')
    return null
  }

  return (
    <>
      <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 md:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8 text-center">
          {t('account.accountInformation')}
        </h1>
        
        <div className="space-y-8">
          {/* Informazioni account */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('account.emailAddress')}
            </h2>
            <p className="text-gray-600">{user.email}</p>
            <ChangePasswordForm user={user} />
          </div>

          {/* Indirizzo personale */}
          <AddressForm user={user} onUpdate={handleUserUpdate} />

          {/* Prenotazioni */}
          <div>
            <UserBookings />
          </div>

          {/* Ordini Prodotti */}
          <div>
            <UserProductOrders />
          </div>

          {/* Azioni account */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('payment.accountActions')}
            </h2>
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => setShowLogoutModal(true)}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
              >
                {t('account.logout')}
              </button>
              
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-6 py-3 border border-red-300 text-red-700 bg-white rounded-lg hover:bg-red-50 transition-colors duration-200"
              >
                {t('account.deleteAccount')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('account.logoutConfirmation')}
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {t('account.logoutConfirmMessage')}
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  {t('account.cancel')}
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {t('account.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('account.deleteAccountConfirmation')}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                {t('account.deleteAccountWarning')}
              </p>
              
              <div className="mb-4 space-y-3">
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => {
                    setDeleteConfirmation(e.target.value)
                    if (deleteError) setDeleteError('')
                  }}
                  placeholder={t('account.deleteAccountInputPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />

                {deleteError && (
                  <p className="text-red-500 text-sm mt-1 text-left">{deleteError}</p>
                )}
              </div>
              
              <div className="flex justify-center space-x-3">
                <button
                  onClick={resetDeleteModal}
                  disabled={isDeleting}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 transition-colors duration-200"
                >
                  {t('account.cancel')}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting || deleteConfirmation !== 'delete'}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 transition-colors duration-200"
                >
                  {isDeleting ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Eliminando...
                    </div>
                  ) : (
                    t('account.deleteAccountButton')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AccountPage 